import React from "react";
import {Drawer, IconButton, Slider, Tooltip} from "@mui/material";
import {ArrowForward, Close, InfoOutlined, RestartAltOutlined} from "@mui/icons-material";
import type {ContractChild} from "../contractCoverageData";
import {COVERAGE_CURRENCY} from "../contractCoverageData";
import {formatCompactCurrency, POSITION_LABEL, positionColors} from "../contractCoverageUtils";
import type {ScoreOverrides} from "../scoringModel";
import {
    baselineValue,
    effectiveValue,
    effectiveWeightedScore,
    needFromScore,
    recomputeChild,
    SCORE_DIMENSIONS,
    SCORE_MAX,
    SCORE_MIN,
    VERDICT_LABEL,
} from "../scoringModel";

type Props = {
    open: boolean;
    onClose: () => void;
    child: ContractChild | null;       // original (un-overridden) child
    overrides: ScoreOverrides;
    onApply: (overrides: ScoreOverrides) => void;
};

export const ScoreReviewDrawer: React.FC<Props> = ({open, onClose, child, overrides, onApply}) => {
    const [working, setWorking] = React.useState<ScoreOverrides>(overrides);

    // Seed working copy when the drawer opens or the reviewed child/overrides change.
    const [prevReset, setPrevReset] = React.useState({open, overrides, child});
    if (prevReset.open !== open || prevReset.overrides !== overrides || prevReset.child !== child) {
        setPrevReset({open, overrides, child});
        if (open) setWorking(overrides);
    }

    if (!child) return null;

    const baseScore = child.weightedScore;
    const effScore = effectiveWeightedScore(child, working);
    const recomputed = recomputeChild(child, working);
    const contracted = child.contractCount > 0;

    const baseCol = positionColors(child.position);
    const effCol = positionColors(recomputed.position);
    const changed = recomputed.position !== child.position || effScore !== baseScore;

    const money = (v: number) => formatCompactCurrency(v, COVERAGE_CURRENCY);

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{className: 'cci-score-paper'}}
                sx={theme => ({zIndex: theme.zIndex.drawer + 2})}>
            <div className="cci-score">
                <header className="cci-score-head">
                    <div>
                        <span className="cci-score-eyebrow">Review &amp; adjust scoring</span>
                        <h3>{child.supplier}</h3>
                        <span className="cci-score-sub">
                            {child.country && <span className="cci-country-chip cci-inline">{child.country}</span>}
                            {money(child.spend)} cumulative spend
                        </span>
                    </div>
                    <IconButton onClick={onClose} size="small"><Close/></IconButton>
                </header>

                <div className="cci-score-body">
                    {/* Verdict before / after */}
                    <div className="cci-verdict">
                        <div className="cci-verdict-side">
                            <span className="cci-verdict-label">Current</span>
                            <span className="cci-verdict-score">{baseScore.toFixed(2)}<small>/5</small></span>
                            <span className="cci-verdict-need">{VERDICT_LABEL[needFromScore(baseScore)]}</span>
                            <span className="cci-position-pill" style={{backgroundColor: baseCol.bg, color: baseCol.fg}}>
                                {POSITION_LABEL[child.position]}
                            </span>
                        </div>
                        <ArrowForward className="cci-verdict-arrow"/>
                        <div className={`cci-verdict-side${changed ? ' is-changed' : ''}`}>
                            <span className="cci-verdict-label">With your changes</span>
                            <span className="cci-verdict-score">{effScore.toFixed(2)}<small>/5</small></span>
                            <span className="cci-verdict-need">{VERDICT_LABEL[needFromScore(effScore)]}</span>
                            <span className="cci-position-pill" style={{backgroundColor: effCol.bg, color: effCol.fg}}>
                                {POSITION_LABEL[recomputed.position]}
                            </span>
                        </div>
                    </div>

                    {contracted && (
                        <p className="cci-score-note">
                            This supplier has a matched contract, so its position stays utilisation-based —
                            score changes update the verdict but not the contract position.
                        </p>
                    )}

                    {/* Dimensions */}
                    <div className="cci-dims">
                        {SCORE_DIMENSIONS.map(dim => {
                            const base = baselineValue(child, dim);
                            const val = effectiveValue(child, dim, working);
                            const isModified = working[dim.key] !== undefined && working[dim.key] !== base;
                            return (
                                <div className={`cci-dim${isModified ? ' is-modified' : ''}`} key={dim.key}>
                                    <div className="cci-dim-head">
                                        <span className="cci-dim-label">
                                            {dim.label}
                                            <Tooltip title={dim.definition} arrow placement="top">
                                                <InfoOutlined className="cci-dim-info" fontSize="inherit"/>
                                            </Tooltip>
                                            {!dim.dataField && <span className="cci-dim-tag">Reviewer-assessed</span>}
                                        </span>
                                        <span className="cci-dim-value">{val.toFixed(1)}</span>
                                    </div>
                                    <p className="cci-dim-def">{dim.definition}</p>
                                    <Slider
                                        size="small"
                                        min={SCORE_MIN}
                                        max={SCORE_MAX}
                                        step={0.1}
                                        marks={[0, 1, 2, 3, 4, 5].map(v => ({value: v, label: `${v}`}))}
                                        value={val}
                                        onChange={(_e, v) => setWorking(w => ({...w, [dim.key]: v as number}))}
                                    />
                                    <span className="cci-dim-base">
                                        {dim.dataField ? `Data baseline ${base.toFixed(2)}` : 'Not assessed (baseline 0)'}
                                        {isModified && <button type="button" className="cci-dim-reset"
                                                               onClick={() => setWorking(w => {
                                                const n = {...w};
                                                delete n[dim.key];
                                                return n;
                                            })}>reset</button>}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <footer className="cci-score-foot">
                    <span className="cci-score-disclaimer">Weights are provisional — pending the methodology workbook.</span>
                    <div className="cci-score-actions">
                        <button type="button" className="cci-score-resetall" onClick={() => setWorking({})}>
                            <RestartAltOutlined fontSize="small"/> Reset
                        </button>
                        <button type="button" className="cci-score-apply" onClick={() => {
                            onApply(working);
                            onClose();
                        }}>
                            Apply &amp; recalculate
                        </button>
                    </div>
                </footer>
            </div>
        </Drawer>
    );
};
